import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Image } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { POSTS } from "./_posts";
import { PublicNav } from "../../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

function formatDate(iso: string): string {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export default function BlogIndex() {
  const router = useRouter();
  // Newest first.
  const sorted = [...POSTS].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>Blog — Dash-Hound Fast CAT Tracker</title>
          <meta name="description" content="Fast CAT guides, training tips, and stories from the Dash-Hound community. Learn how to prep your dog, improve their times, and find your first event." />
          <meta property="og:title" content="Blog — Dash-Hound Fast CAT Tracker" />
          <meta property="og:description" content="Fast CAT guides, training tips, and stories from the Dash-Hound community." />
          <meta property="og:url" content="https://dashhound.app/blog" />
          <meta name="twitter:title" content="Blog — Dash-Hound Fast CAT Tracker" />
          <meta name="twitter:description" content="Fast CAT guides, training tips, and stories from the Dash-Hound community." />
        </Head>
      )}

      <PublicNav />

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Dash-Hound Blog</Text>
          <Text style={styles.heroSubtitle}>Training tips, Fast CAT guides, and stories from the community</Text>
        </View>

        <View style={styles.body}>
          {sorted.map((p) => (
            <TouchableOpacity key={p.slug} style={styles.card} activeOpacity={0.85}
              onPress={() => router.push(`/(auth)/blog/${p.slug}` as any)}>
              {p.featuredImage ? (
                <View style={styles.featuredFrame}>
                  {Platform.OS === "web" ? (
                    // @ts-ignore — raw <img> on web for cleaner crop + lazy load
                    <img src={p.featuredImage} alt={p.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" } as any} />
                  ) : (
                    <Image source={{ uri: `https://dashhound.app${p.featuredImage}` }} style={{ width: "100%", height: "100%" }} resizeMode="cover" />
                  )}
                </View>
              ) : null}
              <View style={styles.cardBody}>
                <Text style={styles.cardCategory}>{p.category.toUpperCase()}</Text>
                <Text style={styles.cardTitle}>{p.title}</Text>
                <Text style={styles.cardExcerpt}>{p.excerpt}</Text>
                <View style={styles.cardMeta}>
                  <Text style={styles.cardMetaText}>{formatDate(p.date)} · {p.readMinutes} min read</Text>
                  <Text style={styles.cardArrow}>Read →</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
          {sorted.length === 0 ? (
            <Text style={{ textAlign: "center", color: "#6B7280", padding: 32 }}>
              Posts coming soon.
            </Text>
          ) : null}
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    paddingTop: Platform.OS === "ios" ? 50 : 14,
  },
  backButton: { width: 60 },
  backText: { color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: "500" },
  headerTitle: { color: WHITE, fontSize: 16, fontWeight: "700" },
  content: { flexGrow: 1 },
  hero: {
    backgroundColor: "#FFF7E6",
    borderBottomWidth: 1,
    borderBottomColor: "#F5DEB3",
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 40,
    alignItems: "center",
  },
  heroTitle: { color: NAVY, fontSize: 32, fontWeight: "900", textAlign: "center" },
  heroSubtitle: { color: "#92400E", fontSize: 15, marginTop: 8, textAlign: "center", maxWidth: 560 },
  body: {
    maxWidth: 760,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 20,
    paddingVertical: 28,
    gap: 18,
  },
  card: {
    backgroundColor: WHITE,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    elevation: 2,
    overflow: "hidden",
  },
  cardBody: { padding: 22 },
  featuredFrame: { width: "100%", aspectRatio: 16 / 9, backgroundColor: "#F3F4F6" },
  cardCategory: { fontSize: 11, fontWeight: "800", color: ORANGE, letterSpacing: 0.8, marginBottom: 6 },
  cardTitle: { fontSize: 22, fontWeight: "800", color: NAVY, marginBottom: 8, lineHeight: 28 },
  cardExcerpt: { fontSize: 15, color: TEXT, lineHeight: 22, marginBottom: 14 },
  cardMeta: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  cardMetaText: { fontSize: 12, color: "#9CA3AF" },
  cardArrow: { fontSize: 14, color: NAVY, fontWeight: "700" },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
